import React, { Component } from 'react';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import DebouncedSearch from 'components/ui/DebouncedSearch';
import { getTranslatedString } from 'helpers/i18n';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import TableWithoutFilters from 'components/ui/TableWithoutFilters';
import CtaCard from 'components/ui/CtaCard';
import StatusTag from 'components/ui/StatusTag';
import { Form as AntdForm } from 'antd';

import { purchaseOrderColumns } from '../../../purchase_orders/components/helpers/purchaseOrderColumns';

const FormTitle = styled.div`
  font-size: 20px;
  font-family: 'Circular-Bold';
  margin-bottom: 1rem;
`;

const FullWidthDebouncedSearch = styled(DebouncedSearch)`
  width: 100%;
`;

const ScrollablePanel = styled.div`
  height: 100%;
  overflow: scroll;
`;

const ColCutoff = styled(Col)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledCtaCard = styled(CtaCard)`
  left: 0;
  right: 0;
  position: absolute;
  margin: auto;
  margin-top: -13px;
`;

class ProjectPurchaseOrders extends Component {
  state = {
    query: '',
    searchLoading: false,
    sortKey: '',
    sortOrder: '',
    offset: 0,
  };

  componentDidMount() {
    if (this.props.selectedProject && this.props.selectedProject.property_id) {
      this.handleSearch();
    }
  }

  handleSearch = async (query = null) => {
    this.setState({ query: query, searchLoading: true });
    await this.props.getAllPurchaseOrders('all', 0, {
      query: query,
      sort_key: 'created_at',
      sort_order: 'desc',
      property_ids: [this.props.selectedProject.property_id],
    });
    this.setState({ searchLoading: false });
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => this.fetchFilteredData()
    );
    if (this.props.tableRef) this.props.tableRef.scrollToTop(0);
  };

  fetchFilteredData = async () => {
    const projectId = this.props.selectedProject.id;
    const { offset, sortKey, sortOrder } = this.state;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    const fetchPromise = this.props.fetchFilteredProjectPurchaseOrders(
      projectId,
      offset,
      filterOptions
    );

    if (this.props.fetchStats) {
      this.props.fetchStats();
    }
    await fetchPromise;
    const self = this;
    this.setState({ offset: self.props.linkedPurchaseOrders.length });
  };

  unlinkPurchaseOrderFromProject = (projectId, data) => {
    this.props.unlinkPurchaseOrderFromProject(projectId, data).then(() => {
      this.props.fetchProject(projectId);
      this.handleSearch();
    });
  };

  linkPurchaseOrderToProject = (value) => {
    const projectId = this.props.selectedProject.id;
    this.props.linkPurchaseOrderToProject(projectId, value).then(() => {
      this.props.fetchProject(projectId);
      this.handleSearch();
    });
  };

  render() {
    const projectId = this.props.selectedProject.id;
    const { offset, sortKey, sortOrder } = this.state;

    const columns = purchaseOrderColumns();
    columns.push({
      title: '',
      dataKey: 'original_id',
      sortable: false,
      width: 200,
      render: ({ data }) => {
        return (
          <CtaCard
            type="button"
            onClick={() => this.unlinkPurchaseOrderFromProject(projectId, data)}
          >
            {getTranslatedString('landlordPortal.maintenance.projects.unlink')}
          </CtaCard>
        );
      },
    });

    const fieldsAboveFold = [
      {
        title: getTranslatedString('landlordPortal.purchaseOrders.itemQty'),
        key: 'total_quantity',
      },
      {
        title: getTranslatedString('landlordPortal.purchaseOrders.vendor'),
        key: 'external_vendor_name',
        renderer: (data) => {
          let vendor_name = data;

          if (data != null && data.length > 15) {
            vendor_name = vendor_name.substring(0, 14) + '...';
          }
          return vendor_name;
        },
      },
    ];

    const fieldsBelowFold = [
      {
        title: getTranslatedString('landlordPortal.purchaseOrders.dueDate'),
        key: 'required_date',
      },
      {
        title: getTranslatedString('landlordPortal.purchaseOrders.dateSubmitted'),
        key: 'created_at',
      },
      {
        title: '',
        key: 'id',
        renderer: (data) => {
          return (
            <StyledCtaCard
              type="button"
              onClick={() => this.unlinkPurchaseOrderFromProject(projectId, data)}
            >
              {getTranslatedString('landlordPortal.maintenance.projects.unlink')}
            </StyledCtaCard>
          );
        },
      },
    ];
    let tableRef = null;
    const setRef = (ref) => (tableRef = ref);
    return (
      <ScrollablePanel>
        <Row gutter={16}>
          <Col md={12} xs={24} style={{ maxWidth: '550px' }}>
            <Spacer pt={1} pr={2} pl={2}>
              <FormTitle>
                {getTranslatedString(
                  'landlordPortal.maintenance.projects.linkPurchaseOrderToThisProject'
                )}
              </FormTitle>
              <AntdForm layout="vertical">
                <Form.Item
                  label={getTranslatedString('landlordPortal.maintenance.projects.purchaseOrder')}
                >
                  <FullWidthDebouncedSearch
                    showSearch
                    placeholder={getTranslatedString(
                      'landlordPortal.maintenance.projects.searchPOToLink'
                    )}
                    size="large"
                    onSearch={this.handleSearch}
                    onSelect={this.linkPurchaseOrderToProject}
                    filterOption={false}
                    notFoundContent={null}
                    loading={this.state.searchLoading}
                    showArrow={false}
                    value={undefined}
                  >
                    {this._renderSelectOptions(this.props.foundPurchaseOrders, projectId)}
                  </FullWidthDebouncedSearch>
                </Form.Item>
              </AntdForm>
            </Spacer>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24}>
            <Spacer mt={0}>
              <TableWithoutFilters
                data={this.props.linkedPurchaseOrders}
                fetchFilteredData={this.fetchFilteredData}
                loadMoreData={this.fetchFilteredData}
                fetchStats={() => this.props.fetchProject(projectId)}
                isLoading={false}
                columns={columns}
                emptyTableText={getTranslatedString(
                  'landlordPortal.maintenance.projects.noLinkedPO'
                )}
                fieldsAboveFold={fieldsAboveFold}
                fieldsBelowFold={fieldsBelowFold}
                mobileTableTitle="title"
                setRef={setRef}
                tableRef={tableRef}
                offset={offset}
                sortKey={sortKey}
                sortOrder={sortOrder}
                handleSort={this.handleSort}
              />
            </Spacer>
          </Col>
        </Row>
      </ScrollablePanel>
    );
  }

  _renderSelectOptions = (foundPurchaseOrders, projectId) => {
    if (!foundPurchaseOrders || foundPurchaseOrders.length === 0)
      return (
        <Select.Option key="1" value="none">
          {getTranslatedString('landlordPortal.purchaseOrders.noneFound')}
        </Select.Option>
      );

    const filteredPurchaseOrders = foundPurchaseOrders.filter(
      (record) => record.project_id !== projectId
    );

    return filteredPurchaseOrders.map((record) => (
      <Select.Option key={`record_${record.id}`} value={record.id}>
        <Row gutter={16}>
          <Col xs={18}>{record.title}</Col>
          <Col xs={6}>
            <span style={{ float: 'right' }}>
              <StatusTag status={record.status} />
            </span>
          </Col>
          <ColCutoff xs={18}>
            <small>{record.external_vendor_name}</small>
          </ColCutoff>
        </Row>
      </Select.Option>
    ));
  };
}

export default ProjectPurchaseOrders;
